import { IVoiceProps } from '@/entities/Voice'
import { INewsProps } from '@/entities/News'
import { microCmsClient } from '@/infra/network/Microcms/APIClient'
import Voice from '@/gateways/MicrocmsGateway/types/voice'
import News from '@/gateways/MicrocmsGateway/types/news'
import FeaturedNews from '@/gateways/MicrocmsGateway/types/featuredNews'
import { toVoiceProps, toNewsProps } from '@/gateways/MicrocmsGateway/translator'

const DEFAULT_PER_PAGE = 4

const ContentType = {
  VOICE: 'voice',
  NEWS: 'news',
  FEATURED_NEWS: 'featured_news'
}

export default class MicrocmsGateway {
  constructor(protected client: typeof microCmsClient) {}

  async getVoice(voiceId: IVoiceProps['id']): Promise<IVoiceProps> {
    try {
      const res = await this.client.get<Voice>({
        endpoint: ContentType.VOICE,
        contentId: voiceId
      })

      return toVoiceProps(res)
    } catch (e) {
      throw e
    }
  }

  async getVoices({ page = 1, perPage = DEFAULT_PER_PAGE }: { page?: number; perPage?: number }): Promise<{ total: number; voices: IVoiceProps[] }> {
    const res = await this.client.getList<Voice>({
      endpoint: ContentType.VOICE,
      queries: {
        offset: (page - 1) * perPage,
        limit: perPage
      }
    })
    return {
      total: res.totalCount,
      voices: res.contents.map(item => toVoiceProps(item))
    }
  }

  async getNewsList({ page = 1, perPage = DEFAULT_PER_PAGE, today }: { page?: number; perPage?: number; today: string | null }): Promise<{ total: number; newsList: INewsProps[] }> {
    const res = await this.client.getList<News>({
      endpoint: ContentType.NEWS,
      queries: {
        limit: perPage,
        offset: (page - 1) * perPage,
        orders: '-startDateTime',
        filters: `endDateTime[not_exists][or]endDateTime[greater_than]${today}`
      }
    })
    return {
      total: res.totalCount,
      newsList: res.contents.map(toNewsProps)
    }
  }

  async getNews(newsId: News['id']): Promise<INewsProps> {
    try {
      const res = await this.client.get<News>({
        endpoint: ContentType.NEWS,
        contentId: newsId
      })
      return toNewsProps(res)
    } catch (e) {
      throw e
    }
  }

  async getFeaturedNews(): Promise<INewsProps | null> {
    const res = await this.client.getList<FeaturedNews>({
      endpoint: ContentType.FEATURED_NEWS,
      queries: {
        limit: 1
      }
    })

    const item = res.contents[0]
    if (!item) return null

    const { featuredNews } = item
    if (!featuredNews) return null

    return toNewsProps(featuredNews)
  }
}

export const MicrocmsGatewayFactory = (): MicrocmsGateway => {
  return new MicrocmsGateway(microCmsClient)
}
